import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const HeroContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1;
    pointer-events: none;
  }
`;

const FallbackImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

const HeroVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  opacity: ${props => (props.$visible ? 1 : 0)};
  pointer-events: none;
  transition: opacity 0.4s ease;
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 900px;
  padding: 0 20px;
`;

const Title = styled.h1`
  font-size: 4.5rem;
  margin-bottom: 2rem;
  text-shadow: 0 0 10px rgba(20, 241, 149, 0.5);
  white-space: nowrap;
  
  @media (max-width: 1024px) {
    font-size: 4rem;
  }

  @media (max-width: 768px) {
    font-size: 3rem;
    white-space: normal;
  }

  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.secondary};
`;

const JoinButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.gradient};
  color: ${props => props.theme.colors.text};
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  box-shadow: 0 4px 15px rgba(20, 241, 149, 0.3);

  i {
    margin-right: 10px;
    font-size: 1.4rem;
  }

  &:hover {
    opacity: 0.9;
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(20, 241, 149, 0.4);
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 1.5rem;
    font-size: 1.1rem;
  }
`;

const Hero = () => {
  const [videoReady, setVideoReady] = useState(false);
  const [showVideo, setShowVideo] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setShowVideo(false);
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    const playVideo = async () => {
      try {
        video.muted = true;
        await video.play();
      } catch {
        setShowVideo(false);
      }
    };

    playVideo();
  }, []);

  return (
    <HeroContainer>
      <FallbackImage
        src="/meme-retirement-home.jpg"
        alt="Meme Retirement Home"
      />
      {showVideo && (
        <HeroVideo
          ref={videoRef}
          src="/mrh-intro.mp4"
          poster="/meme-retirement-home.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          $visible={videoReady}
          onPlaying={() => setVideoReady(true)}
          onError={() => setShowVideo(false)}
        />
      )}
      <Content>
        <Title>Meme Retirement Home</Title>
        <Subtitle>Where old memes go for their golden years in dignity</Subtitle>
        <JoinButton href="https://t.me/memeretire" target="_blank" rel="noopener noreferrer">
          <i className="fab fa-telegram-plane"></i> Join The Community
        </JoinButton>
      </Content>
    </HeroContainer>
  );
};

export default Hero;
